import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, requireImageUrl, brandInitials, PHOTO_GRADE } from "./types";

// venueBold — like venueOverlay (eyebrow top-left, big serif headline, a
// logo + CTA footer) but the headline + footer sit on a short beige textured
// backdrop box at the bottom instead of floating over the photo. Eyebrow,
// title sizing, and footer layout are matched to venueOverlay; only the
// bottom box is unique to this template.
//   eyebrow → small uppercase eyebrow, top-left over the photo
//   name    → the dominant serif headline (in the beige box)
//   brandLogo/brand → footer-left mark   ·   cta → footer-right action
const BAND = "#241f1b"; // photo-area fallback only
const BEIGE = "#f3efe8"; // near-white base (the texture is white grain) so tints read true
const INK = "#2b2520";
const CTA_COLOR = "#b4502c";
const BAND_TEX = "https://pins-sauntera.web.app/stock/band-texture-white.jpg";

// Reduced bottom backdrop box (holds the headline + footer).
const BAND_H = 360;

export function venueBoldTemplate(input: PinInput): string {
  const { brand, brandLogo, eyebrow, cta, typography, venue, bandTint } = input;
  const imageUrl = requireImageUrl(input, "venueBold");
  if (!venue) {
    throw new Error("venueBold template requires venue { name, location?, tagline? }");
  }

  // Title sizing ladder matched to venueOverlay.
  const nameLen = venue.name.length;
  const nameSize = nameLen <= 22 ? 104 : nameLen <= 32 ? 92 : nameLen <= 42 ? 78 : 68;
  const photoH = PIN_HEIGHT - BAND_H;
  // Wash the band's texture with a tint derived from the photo.
  const tintLayer = bandTint ? `linear-gradient(${bandTint}b3,${bandTint}b3),` : "";

  // No-logo monogram fallback. On the LIGHT beige box it's dark ink (not the
  // white treatment overlay uses over a dark photo).
  const monoText = (input.monogramText && input.monogramText.trim()) || brandInitials(brand);
  const monoClass = input.monogramFont === "serif" ? "serif" : "script";
  const monoLen = monoText.length;
  const ringFont = monoLen <= 2 ? 30 : 24;

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:${BAND}}
  .pin{position:relative;width:100%;height:100%;overflow:hidden;background:${BAND}}
  .photo{position:absolute;top:0;left:0;right:0;height:${photoH}px;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:${Math.round((input.focusX ?? 0.5) * 100)}% center;filter:${PHOTO_GRADE}}
  /* Diagonal darken at the TOP-LEFT so the eyebrow reads (matches overlay). */
  .photo::before{content:"";position:absolute;inset:0;background:linear-gradient(135deg,rgba(0,0,0,.55) 0%,rgba(0,0,0,.18) 30%,rgba(0,0,0,0) 52%)}
  /* Eyebrow — matched to venueOverlay (size, tracking, position). */
  .eyebrow{position:absolute;top:64px;left:56px;right:56px;z-index:3;color:#fff;
    font-size:30px;letter-spacing:5px;text-transform:uppercase;font-weight:700;opacity:.94;text-shadow:0 2px 12px rgba(0,0,0,.45)}
  /* Short beige backdrop box: headline up top, footer pinned to the bottom. */
  .band{position:absolute;top:${photoH}px;left:0;right:0;bottom:0;color:${INK};
    background:${tintLayer}${BEIGE} url('${BAND_TEX}') center/cover;
    padding:30px 56px;display:flex;flex-direction:column;justify-content:space-between}
  /* Headline — font + size matched to venueOverlay's .name. */
  .headline{font-family:${typography.headlineFont};font-weight:${typography.headlineWeight};font-size:${nameSize}px;line-height:1.12;letter-spacing:-1.5px;padding-bottom:.14em;
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .headline em{font-style:italic;font-weight:400}
  /* Footer — logo left, CTA right (matches overlay's footer layout). */
  .footer{display:flex;justify-content:space-between;align-items:center;gap:24px}
  .brand-logo{max-height:60px;max-width:62%;width:auto;height:auto;display:block;filter:drop-shadow(0 1px 3px rgba(0,0,0,.18))}
  .brand-text{font-weight:800;letter-spacing:2px;text-transform:uppercase;font-size:24px;color:${INK};opacity:.85;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .monogram{flex-shrink:0;color:${INK};display:flex;align-items:center;justify-content:center}
  .monogram.serif{width:64px;height:64px;border:2.5px solid ${INK};border-radius:50%;font-family:${typography.headlineFont};font-size:${ringFont}px;letter-spacing:${monoLen <= 2 ? 1 : 0}px}
  .monogram.script{font-family:'Great Vibes',cursive;font-size:${monoLen <= 2 ? 56 : 46}px;line-height:1}
  .cta{font-size:28px;font-weight:600;color:${CTA_COLOR};white-space:nowrap}
</style></head>
<body><div class="pin">
  <div class="photo"></div>
  ${eyebrow ? `<div class="eyebrow">${escapeHtml(eyebrow)}</div>` : ""}
  <div class="band">
    <div class="headline">${escapeHtml(venue.name)}</div>
    <div class="footer">
      ${
        input.hideLogo
          ? ""
          : brandLogo
            ? `<img class="brand-logo" src="${brandLogo}" alt="${escapeHtml(brand)}" />`
            : `<span class="monogram ${monoClass}">${escapeHtml(monoText)}</span>`
      }
      ${cta ? `<span class="cta">${escapeHtml(cta)} →</span>` : ""}
    </div>
  </div>
</div></body></html>`;
}
